<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { Trash2, Plus } from "lucide-svelte"
  import Navigation from "./Navigation.svelte"

  import img from "@images/onboarding/departments.jpg"
  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"
  import PrevNextSkipButtons from "$app/Pages/Plans/Onboarding/components/PrevNextSkipButtons.svelte"

  let errors: any = {}

  if (appStore.departments.length === 0) {
    appStore.departments = [
      { name: "", _errors: {}, _saving: false } as DepartmentTypeExt,
      { name: "", _errors: {}, _saving: false } as DepartmentTypeExt
    ]
  }

  const placeholders = [
    "Marketing",
    "Sales",
    "Engineering",
    "Finance",
    "HR",
    "Operations"
  ]

  onboardingStore.completed = false

  // Update department
  async function updateDepartment(
    department: DepartmentTypeExt,
    idx: number
  ): Promise<void> {
    appStore.departments[idx]._errors = {}
    const valid: any = validDepartment(department)
    if (!valid.valid) {
      appStore.departments[idx]._errors = valid
      appStore.departments = [...appStore.departments]
      return
    }

    try {
      department._saving = true
      const resp = await xhr.patch(
        `/plan_onboarding/${appStore.plan.id}/update_department/${department.id}`,
        {
          department: { name: department.name }
        }
      )

      appStore.departments[idx] = {
        ...appStore.departments[idx],
        ...resp.data
      }
      appStore.departments = [...appStore.departments]
      toast(`Department "${department.name}" was updated`, "success")
    } catch (e) {
      Honeybadger.notify(e)
      console.error(e)
      toast(`There was an error updating "${department.name}"`, "error")
    } finally {
      department._saving = false
      appStore.departments = appStore.departments
    }
  }

  function addDepartment(): void {
    appStore.departments = [
      ...appStore.departments,
      { name: "" } as DepartmentTypeExt
    ]
  }

  // Create department
  async function createOrUpdateDepartment(
    department: DepartmentTypeExt,
    name: string,
    idx: number
  ): Promise<void> {
    department._errors = {}
    if (department.id && name.trim() !== department.name.trim()) {
      //console.log("update", name.trim(), department.name.trim())
      updateDepartment({ ...department, name }, idx)
      return
    }
    if (!department.id) {
      createDepartment({ name: name } as DepartmentTypeExt, idx)
      return
    }
  }

  async function createDepartment(department: DepartmentTypeExt, idx: number) {
    appStore.departments[idx]._errors = {}

    const valid: any = validDepartment(department)

    if (!valid.valid) {
      appStore.departments[idx]._errors = valid
      appStore.departments = appStore.departments
      return
    }

    try {
      department._saving = true
      const resp = await xhr.post(
        `/plan_onboarding/${appStore.plan.id}/create_department`,
        {
          department: { name: department.name }
        }
      )
      appStore.departments[idx] = {
        ...appStore.departments[idx],
        ...resp.data
      }

      toast(`Department "${department.name}" was created`, "success")
    } catch (e) {
      console.error(e)
      toast(
        `There was an error saving department "${department.name}"`,
        "error"
      )
    } finally {
      department._saving = false
      appStore.departments = appStore.departments
    }
  }

  function validDepartment(department: DepartmentTypeExt): object {
    if (department.name?.trim().length < 3) {
      return { name: "Name must be at least 3 chars" }
    }
    if (
      appStore.departments
        .filter((d) => d.id !== department.id)
        .map((d) => d.name)
        .includes(department.name)
    ) {
      return { name: "There is a department with the same name" }
    }

    return { valid: true }
  }

  // Delete deparment
  async function deleteDepartment(
    department: DepartmentTypeExt,
    idx: number
  ): Promise<void> {
    if (!department.id) {
      appStore.departments.splice(idx, 1)
      appStore.departments = appStore.departments
      return
    }
    department._selected = true
    appStore.departments = appStore.departments
    await wait(50)
    if (!confirm("Are you sure you want to delete the department?")) {
      department._selected = false
      appStore.departments = appStore.departments
      return
    }

    try {
      await xhr.delete(
        `/plan_onboarding/${appStore.plan.id}/delete_department/${department.id}`
      )
      toast(`Department "${department.name}" was deleted`, "success")
      const idx = appStore.departments.findIndex((d) => d.id === department.id)
      appStore.departments.splice(idx, 1)
    } catch (e) {
      Honeybadger.notify(e)
      department._selected = false
      toast("There was an error deleting the selected department", "error")
    } finally {
      appStore.departments = appStore.departments
    }
  }

  onboardingStore.completed =
    appStore.departments.filter((d) => d.id).length > 0

  onMount(() => {
    onboardingStore.setBrowserHistory({}, "departments")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full lg:w-1/2 xl:pr-16">
    <Navigation step="departments" on:updateStep />

    <h2 class="mt-12 text-5xl font-bold text-left text-white">
      Set Up Your Departments
    </h2>

    <p class="mt-8 text-white text-lg max-w-lg">
      {#if appStore.subscription.subscription_type === "free"}
        We have created some default departments for you, please update as
        needed for your organization. <span class="font-bold">
          As a Free Subscription, you are limited to 4 Departments. Later you
          can upgrade to a Standard Subscription and add more.</span
        >
      {:else}
        We have created some default departments for you, please update as
        needed for your organization. We recommend no more than 8 total
        departments.
      {/if}
    </p>

    <div class="mt-8 max-w-lg departments-list">
      {#each appStore.departments as department, idx}
        <div class="flex w-full gap-1">
          <div class="flex-grow input-cont" class:error={errors.name}>
            <input
              type="text"
              value={department.name}
              onblur={(e) => {
                createOrUpdateDepartment(department, e.target.value, idx)
              }}
              class="input flex-auto"
              class:bg-blue-300={department._selected}
              name="statement-name"
              placeholder="Department name {placeholders[idx]
                ? `(E.G. ${placeholders[idx]})`
                : ''}"
            />
            <div class="text-red-500">{department._errors?.name || ""}</div>
          </div>
          <button
            onclick={() => deleteDepartment(department, idx)}
            type="button"
            class="p-1 ml-2"
          >
            <Trash2 class="size-6 text-gray-300" />
          </button>
        </div>
      {:else}
        <p
          class="text-lg text-white bg-red-400 rounded border border-red-500 py-2 px-4"
        >
          You need at least one Department to proceed.
        </p>
      {/each}

      {#if appStore.subscription.subscription_type === "free" && appStore.departments.length >= 4}
        <p class="mt-6 text-gray-200 font-semibold text-sm">
          You have reached the max of 4 departments for a free plan
        </p>
      {:else}
        <button
          data-name="add-department"
          type="button"
          class="text-white mt-6"
          onclick={() => addDepartment()}
        >
          <div class="flex items-center gap-1 mb-1">
            <Plus class="text-gray-300 size-6" />
            <span class="label">Add Department</span>
          </div>
        </button>
      {/if}
    </div>

    <PrevNextSkipButtons showSkip={false} />
  </div>
</div>
<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-0 lg:-right-0 md:w-1/3 lg:w-1/2 lg:h-full bg-lwp-light-grey bg-center bg-no-repeat bg-cover"
  style="background-image: url({img})"
></div>
